import { useMemo } from 'react';

import { Capability } from '@/hooks/useCapabilities';

interface UseDrawerValidationProps {
  capability: Capability | null;
  selectedAgent: string;
  selectedTargets: string[];
  parameterValues: Record<string, string>;
  validationErrors: Record<string, boolean>;
}

interface UseDrawerValidationReturn {
  isConfigValid: boolean;
  hasTargets: boolean;
  canExecute: boolean;
  validationSummary: string;
  missingFields: string[];
}

/**
 * useDrawerValidation - Single source of truth for drawer validation state
 *
 * Provides computed validation state for:
 * - Configuration (parameters + agent for internal capabilities)
 * - Target selection
 * - Overall execution readiness
 */
export function useDrawerValidation({
  capability,
  selectedAgent,
  selectedTargets,
  parameterValues,
  validationErrors: _validationErrors,
}: UseDrawerValidationProps): UseDrawerValidationReturn {
  const isInternalSurface = capability?.surface === 'internal';
  const hasInput = !capability?.target?.includes('noinput');

  // Check if all required parameters are filled
  const { isConfigValid, missingFields } = useMemo(() => {
    if (!capability) {
      return { isConfigValid: false, missingFields: [] };
    }

    // For internal capabilities, agent is required
    if (isInternalSurface && !selectedAgent) {
      return { isConfigValid: false, missingFields: ['Agent'] };
    }

    // Check required parameters
    const requiredParams =
      capability.parameters?.filter(param => param.required) || [];
    const missing: string[] = [];

    for (const param of requiredParams) {
      const value = parameterValues[param.name]?.trim();
      if (!value) {
        missing.push(param.name);
      }
    }

    return {
      isConfigValid: missing.length === 0,
      missingFields: missing,
    };
  }, [capability, selectedAgent, parameterValues, isInternalSurface]);

  // Check if targets are selected (or not needed for noinput capabilities)
  const hasTargets = useMemo(() => {
    if (!hasInput) {
      return true; // No targets needed for noinput capabilities
    }
    return selectedTargets.length > 0;
  }, [hasInput, selectedTargets]);

  // Overall execution readiness
  const canExecute = isConfigValid && hasTargets;

  // Generate validation summary message
  const validationSummary = useMemo(() => {
    if (canExecute) {
      return 'Ready to execute';
    }

    // Priority order for error messages
    if (isInternalSurface && !selectedAgent) {
      return 'Select an Aegis agent';
    }

    if (missingFields.length > 0) {
      return `Complete required fields: ${missingFields.join(', ')}`;
    }

    if (!hasTargets) {
      return 'Select at least 1 target';
    }

    return 'Complete all required fields';
  }, [canExecute, isInternalSurface, selectedAgent, missingFields, hasTargets]);

  return {
    isConfigValid,
    hasTargets,
    canExecute,
    validationSummary,
    missingFields,
  };
}
